import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  DialogAction,
  DialogPortal,
  DialogOverlay,
} from '@ubiquitel/ui';

import { useAuth } from '../../auth/AuthProvider';
import {
  claimDevice,
  listDevices,
  removeDevice,
  renameDevice,
  type DeviceRow,
} from '../../services/devices';
import type { RealtimeClient } from '../../services/realtime-client';
import { UbiverseClient, UbiverseEventTopicList } from '../../types';

import './index.scss';

interface DevicesProps {
  realtimeClient: RealtimeClient;
}

const Devices = ({ realtimeClient }: DevicesProps) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isClaimOpen, setIsClaimOpen] = useState(false);
  const [pairingCode, setPairingCode] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState<Record<string, string>>({});
  // hardware_ids currently on the channel's presence roster with
  // kind:'device'. Presence sync/leave events keep this live, so Online flips
  // instantly in both directions — no heartbeat broadcasts, no polling.
  const [onlineHardwareIds, setOnlineHardwareIds] = useState<Set<string>>(new Set());

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const rows = await listDevices();
    setDevices(rows);
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    refresh();
  }, [isAuthenticated, navigate, refresh]);

  // Liveness: an ONLINE device (firmware or /sim emulating one) tracks Phoenix
  // presence with kind:'device' + its hardware_id. subscribeEvent fires the
  // callback once with the current roster on subscribe, so there's no gap on
  // mount; the channel's presence state carries membership from before this
  // page was opened.
  useEffect(() => {
    if (!isAuthenticated) return;
    realtimeClient.subscribeEvent(
      UbiverseEventTopicList.SessionListChanged,
      (clients: UbiverseClient[]) => {
        setOnlineHardwareIds(new Set(
          clients
            .filter((c) => c.kind === 'device' && c.hardware_id)
            .map((c) => c.hardware_id!),
        ));
      },
    );
    return () => realtimeClient.unsubscribeEvent(UbiverseEventTopicList.SessionListChanged);
  }, [isAuthenticated, realtimeClient]);

  const handleClaim = async () => {
    setClaimBusy(true);
    setClaimError(null);
    try {
      await claimDevice(pairingCode, deviceName || undefined);
      setPairingCode('');
      setDeviceName('');
      setIsClaimOpen(false);
      await refresh();
    } catch (err: any) {
      setClaimError(err?.message || 'Claim failed.');
    } finally {
      setClaimBusy(false);
    }
  };

  const handleRenameCommit = async (id: string) => {
    const next = renameDraft[id]?.trim();
    if (!next) return;
    try {
      await renameDevice(id, next);
      setRenameDraft((prev) => {
        const { [id]: _gone, ...rest } = prev;
        return rest;
      });
      await refresh();
    } catch (err: any) {
      console.error('rename failed', err);
    }
  };

  const handleRemove = async (id: string, name: string, hardwareId: string) => {
    if (!window.confirm(`Remove "${name}"? The device will need to be re-paired.`)) return;
    try {
      await removeDevice(id);
      // Instant kick (docs/device-auth.md "Revocation latency"): tell the live
      // device its claim is gone. It verifies against /device-status before
      // wiping, so a spoofed broadcast can't unprovision a still-claimed device.
      realtimeClient.emitEvent(UbiverseEventTopicList.DeviceRevoked, { hardware_id: hardwareId });
      await refresh();
    } catch (err: any) {
      console.error('remove failed', err);
    }
  };

  return (
    <div className="page--devices">
      <header className="page--devices__header">
        <div>
          <Link to="/" className="page--devices__back">← Back to dashboard</Link>
          <h1 className="page--devices__title">Devices</h1>
          <p className="page--devices__subtitle">
            ESP devices claimed by your account.
          </p>
        </div>
        <Button onClick={() => setIsClaimOpen(true)}>Add device</Button>
      </header>

      <section className="page--devices__list">
        {loading && <p className="page--devices__empty">Loading…</p>}
        {!loading && devices.length === 0 && (
          <p className="page--devices__empty">
            No devices yet. Click <strong>Add device</strong> and enter the pairing code shown
            on the device's serial console.
          </p>
        )}
        {!loading &&
          devices.map((d) => {
            const isOnline = onlineHardwareIds.has(d.hardware_id);
            const isRenaming = renameDraft[d.id] !== undefined;
            return (
              <article key={d.id} className="page--devices__row">
                <div className={`page--devices__status ${isOnline ? 'online' : 'offline'}`}>
                  <span className="page--devices__status-dot" />
                  {isOnline ? 'Online' : d.last_seen ? 'Offline' : 'Never connected'}
                </div>
                <div className="page--devices__name-cell">
                  {isRenaming ? (
                    <input
                      className="page--devices__name-input"
                      value={renameDraft[d.id]}
                      autoFocus
                      onChange={(e) =>
                        setRenameDraft((prev) => ({ ...prev, [d.id]: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameCommit(d.id);
                        if (e.key === 'Escape') {
                          setRenameDraft((prev) => {
                            const { [d.id]: _gone, ...rest } = prev;
                            return rest;
                          });
                        }
                      }}
                      onBlur={() => handleRenameCommit(d.id)}
                    />
                  ) : (
                    <button
                      type="button"
                      className="page--devices__name"
                      onClick={() =>
                        setRenameDraft((prev) => ({ ...prev, [d.id]: d.name }))
                      }
                    >
                      {d.name}
                    </button>
                  )}
                  <code className="page--devices__hardware-id">{d.hardware_id}</code>
                </div>
                <div className="page--devices__last-seen">
                  {d.last_seen
                    ? `Last seen ${new Date(d.last_seen).toLocaleString()}`
                    : `Claimed ${new Date(d.claimed_at!).toLocaleString()}`}
                </div>
                <div className="page--devices__actions">
                  <Button variant="ghost" onClick={() => handleRemove(d.id, d.name, d.hardware_id)}>
                    Remove
                  </Button>
                </div>
              </article>
            );
          })}
      </section>

      <Dialog open={isClaimOpen} onOpenChange={setIsClaimOpen}>
        <DialogPortal>
          <DialogOverlay />
          <DialogContent
            heading="Add device"
            description="Enter the pairing code shown on the device's serial console."
          >
            <div className="page--devices__claim-form">
              <TextField
                label="Pairing code"
                placeholder="8F3-2KQ"
                value={pairingCode}
                onChange={(e) => setPairingCode(e.target.value)}
                size="full"
              />
              <TextField
                label="Device name (optional)"
                placeholder="Left finger"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                size="full"
              />
              {claimError && (
                <p className="page--devices__claim-error">{claimError}</p>
              )}
            </div>
            <DialogActions>
              <DialogAction onClick={() => setIsClaimOpen(false)}>Cancel</DialogAction>
              <DialogAction
                className="primary"
                onClick={handleClaim}
                disabled={claimBusy || !pairingCode.trim()}
              >
                {claimBusy ? 'Claiming…' : 'Claim device'}
              </DialogAction>
            </DialogActions>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </div>
  );
};

export default Devices;
